import { z } from 'zod';
import { UtcDateTimeSchema } from './timestamps.js';
export const TillSessionStatusSchema = z.enum(['OPEN', 'CLOSED']);
export const TillReportTypeSchema = z.enum(['X', 'Z']);
export const TillTenderTypeSchema = z.enum(['CASH', 'CARD', 'PAYPAL', 'BACS', 'CHEQUE', 'ACCOUNT']);
const PenceSchema = z.number().int().min(-9_000_000_000_000).max(9_000_000_000_000);
const NonNegativePenceSchema = PenceSchema.min(0);
export const OpenTillSessionRequestSchema = z.object({
    operatorUserId: z.uuid().optional(),
    deviceId: z.uuid().optional(),
    notes: z.string().trim().max(1000).nullable().optional(),
});
export const RecordTillCashCountRequestSchema = z.object({
    operatorUserId: z.uuid().optional(),
    countedCashPence: NonNegativePenceSchema,
    note: z.string().trim().max(1000).nullable().optional(),
});
export const CloseTillSessionRequestSchema = z.object({
    operatorUserId: z.uuid().optional(),
    countedCashPence: NonNegativePenceSchema,
    varianceReason: z.string().trim().max(1000).nullable().optional(),
    notes: z.string().trim().max(1000).nullable().optional(),
}).superRefine((value, context) => {
    if (value.varianceReason !== undefined && value.varianceReason !== null && value.varianceReason.length === 0) {
        context.addIssue({ code: 'custom', path: ['varianceReason'], message: 'Variance reason cannot be blank' });
    }
});
export const TillSessionListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25),
    from: z.iso.date().optional(), to: z.iso.date().optional(), locationId: z.uuid().optional(), deviceId: z.uuid().optional(),
    operatorUserId: z.uuid().optional(), status: TillSessionStatusSchema.optional(), varianceOnly: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
});
export const TillTenderTotalSchema = z.object({
    tenderType: TillTenderTypeSchema,
    grossReceiptsPence: NonNegativePenceSchema,
    refundsPence: NonNegativePenceSchema,
    netPence: PenceSchema,
});
export const TillLinkedTransactionSchema = z.object({
    kind: z.enum(['SALE', 'REFUND', 'ACCOUNT_PAYMENT']), id: z.uuid(), number: z.string(), occurredAt: UtcDateTimeSchema,
    amountPence: PenceSchema, tenderSummary: z.string(),
});
export const TillCashCountSchema = z.object({
    id: z.uuid(), countedCashPence: NonNegativePenceSchema, countedByUserId: z.uuid(), countedByName: z.string(),
    countedAt: UtcDateTimeSchema, note: z.string().nullable(),
});
export const TillSessionReportSchema = z.object({
    reportType: TillReportTypeSchema,
    label: z.string(),
    sessionId: z.uuid(), sessionNumber: z.string(), status: TillSessionStatusSchema,
    locationId: z.uuid(), locationCode: z.string(), locationName: z.string(),
    deviceId: z.uuid(), deviceName: z.string(),
    openedByUserId: z.uuid(), openedByName: z.string(), openedAt: UtcDateTimeSchema,
    closedByUserId: z.uuid().nullable(), closedByName: z.string().nullable(), closedAt: UtcDateTimeSchema.nullable(),
    durationSeconds: z.number().int().nonnegative(), openingFloatPence: NonNegativePenceSchema,
    grossSalesPence: NonNegativePenceSchema, refundsPence: NonNegativePenceSchema, netSalesPence: PenceSchema,
    netExGstPence: PenceSchema, gstPence: PenceSchema, transactionCount: z.number().int().nonnegative(), refundCount: z.number().int().nonnegative(),
    accountSalesPence: PenceSchema, accountPaymentsPence: NonNegativePenceSchema,
    tenderTotals: z.array(TillTenderTotalSchema).length(6), expectedCashPence: PenceSchema,
    countedCashPence: NonNegativePenceSchema.nullable(), cashVariancePence: PenceSchema.nullable(), varianceReason: z.string().nullable(),
    notes: z.string().nullable(), latestCount: TillCashCountSchema.nullable(), generatedAt: UtcDateTimeSchema,
});
export const TillSessionSummarySchema = TillSessionReportSchema.pick({
    sessionId: true, sessionNumber: true, status: true, locationId: true, locationName: true, deviceId: true, deviceName: true,
    openedByUserId: true, openedByName: true, openedAt: true, closedAt: true, expectedCashPence: true,
    countedCashPence: true, cashVariancePence: true, tenderTotals: true,
});
export const TillSessionDetailSchema = z.object({ report: TillSessionReportSchema, transactions: z.array(TillLinkedTransactionSchema), printCount: z.object({ X: z.number().int().nonnegative(), Z: z.number().int().nonnegative() }) });
export const TillSessionListResponseSchema = z.object({ items: z.array(TillSessionSummarySchema), page: z.number().int(), pageSize: z.number().int(), total: z.number().int().nonnegative() });
export const TillSessionCommandResponseSchema = z.object({ report: TillSessionReportSchema, replayed: z.boolean() });
